import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { LuRotateCw, LuSearch } from "react-icons/lu";
import api from "../../api/axios";
import NotificationBell from "../Common/NotificationBell";
import DateFilter from "../Common/DateFilter";
import DashboardChartModal from "./DashboardChartModal";
import TooltipProvider from "./DashboardTooltip";
import TopOrgsFlipCards from "./TopOrgsFlipCards";
import KpiCards from "./KpiCards";
import OrgDealValueCard from "./OrgDealValueCard";
import OwnerColumnChartCard from "./OwnerColumnChartCard";
import TeamPerformanceCard from "./TeamPerformanceCard";
import TaskDonutCard from "./TaskDonutCard";
import {
  PRESETS,
  calculatePresetRange,
  buildDateFilterParams,
} from "../../utils/dateFilterUtils";
import "../../assets/css/Dashboard/Dashboard.css";

export default function Dashboard() {
  // Initialize DateFilter with default "This Month"
  const [dateFilter, setDateFilter] = useState(() =>
    calculatePresetRange(PRESETS.THIS_MONTH)
  );
  const [selectedUserId, setSelectedUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartConfig, setChartConfig] = useState(null);
  const requestIdRef = useRef(0);

  // Authenticated user session
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const roleDisplay = useMemo(() => {
    const role = currentUser?.role || "";
    return role.replace(/_/g, " ").toUpperCase();
  }, [currentUser]);

  // Fetch Dashboard Overview from API
  const fetchOverview = useCallback(
    async (activeFilter = dateFilter, userId = selectedUserId) => {
      const currentRequestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);

      try {
        let params = buildDateFilterParams(activeFilter);
        if (userId) {
          params.user_id = userId;
        }

        const res = await api.get("/dashboard/overview", { params });

        let renewalRevenue = res.data?.kpis?.renewal_revenue;
        if (renewalRevenue === undefined) {
          try {
            const renewalRes = await api.get("/renewal/cards", { params });
            if (renewalRes && renewalRes.data) {
              renewalRevenue = renewalRes.data.revenue ?? 0;
            }
          } catch {
            // fallback
          }
        }

        if (currentRequestId === requestIdRef.current) {
          setDashboardData({
            ...res.data,
            renewal_revenue: renewalRevenue ?? 0,
          });
        }
      } catch (err) {
        if (currentRequestId === requestIdRef.current) {
          console.error("Failed to load dashboard overview:", err);
          setError("Unable to load dashboard data. Please try again.");
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [dateFilter, selectedUserId]
  );

  useEffect(() => {
    fetchOverview(dateFilter, selectedUserId);
  }, [dateFilter, selectedUserId, fetchOverview]);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
  };

  const handleUserChange = (e) => {
    setSelectedUserId(e.target.value);
  };

  const organizations = dashboardData?.organizations || [];
  const organizationUsers = dashboardData?.organization_users || [];
  const tasks = dashboardData?.tasks || [];
  const kpis = dashboardData?.kpis || {};
  const canFilterEmployees = dashboardData?.can_filter_employees || false;
  const availableEmployees = dashboardData?.available_employees || [];

  const selectedUserName = useMemo(() => {
    if (!selectedUserId) return null;
    const emp = availableEmployees.find((e) => String(e.id) === String(selectedUserId));
    return emp ? emp.name : null;
  }, [selectedUserId, availableEmployees]);

  const totalDealValue = useMemo(() => {
    if (kpis?.total_deal_value) return kpis.total_deal_value;
    return organizations.reduce(
      (acc, curr) => acc + (curr.deal_price ?? curr.price ?? 0),
      0
    );
  }, [kpis, organizations]);

  const totalDeals = useMemo(() => {
    if (kpis?.total_deals !== undefined && kpis?.total_deals !== null) {
      return kpis.total_deals;
    }
    const orgDeals = organizations.reduce((acc, curr) => acc + (curr.deals ?? 0), 0);
    if (orgDeals > 0) return orgDeals;
    return organizationUsers.reduce((acc, curr) => acc + (curr.deals ?? 0), 0);
  }, [kpis, organizations, organizationUsers]);

  const renewalRevenue = useMemo(() => {
    return kpis?.renewal_revenue ?? dashboardData?.renewal_revenue ?? 0;
  }, [kpis, dashboardData]);

  const totalLeads = useMemo(() => {
    if (kpis?.total_leads) return kpis.total_leads;
    return organizationUsers.reduce((acc, curr) => acc + (curr.leads ?? 0), 0);
  }, [kpis, organizationUsers]);

  const handleOpenChart = (type, title) => {
    setChartConfig({ type, title });
  };

  const handleCloseChart = () => {
    setChartConfig(null);
  };

  const activeChartData = useMemo(() => {
    if (!chartConfig) return [];
    if (chartConfig.type === "organization") return organizations;
    if (chartConfig.type === "user") return organizationUsers;
    if (chartConfig.type === "task") return tasks;
    return [];
  }, [chartConfig, organizations, organizationUsers, tasks]);

  return (
    <TooltipProvider>
      <div className="dashboard-page" data-testid="dashboard-container">
        {/* Page Header */}
        <div className="dashboard-page-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              Dashboard
              {roleDisplay && (
                <span className="pill">{roleDisplay}</span>
              )}
            </h1>
          </div>

          {/* Action Toolbar */}
          <div className="dashboard-filter-toolbar">
            {/* Search Input for Organizations */}
            <div className="search" data-testid="dashboard-search-box">
              <LuSearch />
              <input
                type="text"
                id="searchInput"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search organizations"
              />
            </div>

            {/* Notification Bell */}
            <div
              className="dashboard-notification-wrapper"
              data-testid="dashboard-notification-bell"
            >
              <NotificationBell />
            </div>

            {/* Optional Employee Filter */}
            {canFilterEmployees && (
              <div
                className="employee-filter-control"
                data-testid="employee-filter-box"
              >
                <select
                  id="userFilter"
                  className="filter-select"
                  value={selectedUserId}
                  onChange={handleUserChange}
                  disabled={loading}
                  data-testid="employee-filter-select"
                  aria-label="Filter by user"
                >
                  <option value="">All Users</option>
                  {availableEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} ({emp.role ? emp.role.replace(/_/g, " ") : "User"})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Date Filter */}
            <div
              className="dashboard-date-filter-wrapper"
              data-testid="dashboard-date-filter"
            >
              <DateFilter
                value={dateFilter}
                onChange={handleDateFilterChange}
                disabled={loading}
              />
            </div>

            {/* Refresh Sync Button */}
            <button
              type="button"
              className={`btn-dashboard-sync ${loading ? "spinning" : ""}`}
              onClick={() => fetchOverview(dateFilter, selectedUserId)}
              title="Refresh dashboard data"
              disabled={loading}
              data-testid="dashboard-refresh-btn"
              aria-label="Refresh dashboard data"
            >
              <LuRotateCw />
            </button>
          </div>
        </div>

        {/* Error Alert Banner */}
        {error && (
          <div className="dashboard-error-banner" data-testid="dashboard-error-banner">
            <span className="dashboard-error-text">{error}</span>
            <button
              type="button"
              className="dashboard-retry-btn"
              onClick={() => fetchOverview(dateFilter, selectedUserId)}
              data-testid="dashboard-retry-btn"
            >
              Retry
            </button>
          </div>
        )}

        {/* Top Organizations 3D Flip Cards Showcase */}
        <TopOrgsFlipCards
          organizations={organizations}
          totalDealValue={totalDealValue}
          loading={loading && !dashboardData}
        />

        {/* 4 KPI Summary Cards */}
        <KpiCards
          kpis={kpis}
          totalOrganizations={organizations.length}
          totalDealValue={totalDealValue}
          totalDeals={totalDeals}
          renewalRevenue={renewalRevenue}
          totalLeads={totalLeads}
          userCount={availableEmployees.length || organizationUsers.length}
          selectedUserName={selectedUserName}
          loading={loading && !dashboardData}
        />

        {/* 12-Column Visual Grid */}
        <div className="dashboard-grid">
          {/* Card 1: Deal Value by Organization (c7) */}
          <OrgDealValueCard
            organizations={organizations}
            totalDealValue={totalDealValue}
            searchQuery={searchQuery}
            loading={loading && !dashboardData}
          />

          {/* Card 2: Deal Value by Owner (c5) */}
          <OwnerColumnChartCard
            organizationUsers={organizationUsers}
            loading={loading && !dashboardData}
          />

          {/* Card 3: Team Performance Table (c8) */}
          <TeamPerformanceCard
            organizationUsers={organizationUsers}
            loading={loading && !dashboardData}
          />

          {/* Card 4: Tasks by User Donut Chart (c4) */}
          <TaskDonutCard
            tasks={tasks}
            loading={loading && !dashboardData}
          />
        </div>

        {/* Chart Dialog / Modal for Drilldown */}
        <DashboardChartModal
          isOpen={Boolean(chartConfig)}
          onClose={handleCloseChart}
          title={chartConfig?.title || ""}
          type={chartConfig?.type || ""}
          data={activeChartData}
        />
      </div>
    </TooltipProvider>
  );
}
